(ns frontend.view.grid.contract-kyc-grid.events
  (:require
    [common.date-formats :as date-formats]
    [common.localization :as loc]
    [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
    [common.schema.entities.workspace.kyc.templates.template-registry :as template-registry]
    [common.schema.entity-types :as entity-types]
    [common.schema.registry :as registry]
    [common.timer :as timer]
    [common.timezones :as timezones]
    [common.util :as cu]
    [frontend.actions.kyc-profile :as kyc-actions]
    [frontend.actions.named-view :as named-view-actions]
    [frontend.conn :as conn]
    [frontend.exports.excel :as excel]
    [frontend.exports.exceljs.workbook :as ex-workbook]
    [frontend.local-storage :as ls]
    [frontend.logging :as logging]
    [frontend.react.virtualized.scrollable-grid.common :as grid]
    [frontend.state.current-session :as session]
    [frontend.state.permissions :as f-permissions]
    [frontend.tenant-customizations :as tenant-customizations]
    [frontend.utils :as fu]
    [frontend.view.grid.companies-grid.events :as companies-events]
    [frontend.view.grid.contract-kyc-grid.columns :as columns]
    [frontend.view.grid.contract-kyc-grid.db :as db]
    [frontend.view.modals.confirmation.view :as confirmation]
    [frontend.view.modals.events :as modals]
    [frontend.zap-time :as zap-time]
    [potpuri.core :refer [fn->]]
    [promesa.core :as p]
    [reagent.core :as r]))

(defn ls-path
  [& rest]
  (concat
    [(:db/id @session/account)
     (:db/id @session/tenant)
     :contract-kyc-profiles-view-settings]
    rest))

(defn precalculated-schema
  [recalculate?]
  (let [calculated @db/schema
        tenant-schema @session/tenant-schema
        schema
        (cond
          (and (not recalculate?)
               calculated)
          calculated
          :else
          (->>
            (registry/get-schema @session/tenant-schema :entity.type/contract-kyc-profile)
            (remove
              (fn [[k v]]
                (let [grid-visibility (->> v
                                           :field/default-visibility
                                           :grid)]
                  (or (nil? grid-visibility)
                      (= grid-visibility :editor.type/none)
                      (if (or (not= (:field/data-type v) :data.type/currency-symbol)
                              (not (:field/secondary-field? v)))
                        (not (f-permissions/field-read-access? k :entity.type/contract-kyc-profile nil))
                        (not (f-permissions/field-read-access? (:field/related-fields v)
                                                               :entity.type/contract-kyc-profile
                                                               nil)))
                      (and (= k :contract-kyc-profile/screening-enabled?)
                           (not (session/screening?)))))))
            (sort
              (fn [[attribute-a _] [attribute-b _]]
                (cond                                       ;; show name first
                  (= :contract-kyc-profile/name attribute-a)
                  -1
                  (= :contract-kyc-profile/name attribute-b)
                  1
                  :else
                  (compare (let [fs (registry/get-field-schema tenant-schema attribute-a)]
                             (common.localization/field-name attribute-a fs))
                           (let [fs (registry/get-field-schema tenant-schema attribute-b)]
                             (common.localization/field-name attribute-b fs))))))
            (doall)))
        company-schema (when (or recalculate?
                                 (not calculated))
                         (companies-events/precalculated-schema recalculate?))

        target-schema (->> company-schema
                           (map (fn [[k v]]
                                  [[:contract-kyc-profile/target k] v]))
                           (into {})
                           (remove (fn-> first (= [:contract-kyc-profile/target :asset/name])))
                           (sort-by (fn [[_ v]]
                                      (-> v
                                          :field/translations
                                          :en)))
                           doall)
        schema (if (and (not recalculate?)
                        calculated)
                 schema
                 (concat schema target-schema))]
    (when (or recalculate?
              (not calculated))
      (reset! db/schema schema))
    schema))

;;precalculate for performance reasons
(defn keys-and-schemas
  []
  (precalculated-schema false))

(defn kyc-profiles-column-keys
  []
  (let [schemas (keys-and-schemas)
        col-keys (concat
                   (when (session/is-admin?) [:db/id])
                   (map first schemas))]
    col-keys))

(def default-kanban-attributes [:contract-kyc-profile/name :contract-kyc-profile/status])

(defn visible-keys-and-schemas
  []
  (let [{:keys [attributes footer-1 footer-2]} @db/card-settings
        visible-attributes
        (set (concat @db/selected-columns
                     attributes
                     default-kanban-attributes
                     [footer-1 footer-2
                      (entity-types/type->owner-attribute :entity.type/contract-kyc-profile)]))]
    (->> (keys-and-schemas)
         (filter (fn [[k _fs]]
                   (some #{k} visible-attributes))))))

(declare expanded? toggle-expand!)

;;necessary arguments to use common grid functions
(defn setup-args
  []
  {:state                       db/contract-kyc-profiles
   :entity-type                 :entity.type/contract-kyc-profile
   :modal-state                 db/modal-state
   :selected-columns-state      db/selected-columns
   :ordered-columns-state       db/ordered-columns
   :filters-state               db/contract-kyc-profile-filters
   :view-settings-state         db/view-settings
   :column-widths-state         db/column-widths
   :selected-pipeline           nil
   :ls-path                     ls-path
   :kanban?                     true
   :kanban-args                 {:default-grouped-by           :contract-kyc-profile/status
                                 :kanban-state                 db/kanban
                                 :grouped-by-state             db/grouped-by
                                 :card-settings-state          db/card-settings
                                 ;;for long card view:
                                 :default-attributes           default-kanban-attributes
                                 :expanded-state               db/expanded
                                 :sorted-kanban-entities-state db/sorted-kanban-entities
                                 :lists-state                  db/lists
                                 :expanded?                    expanded?
                                 :toggle-expand!               toggle-expand!}
   :column-keys                 kyc-profiles-column-keys
   :default-columns             (columns/get-default-columns)
   :mandatory?                  columns/mandatory?
   :visible-keys-and-schemas-fn visible-keys-and-schemas})

(defn expanded?
  [entity-id]
  (let [card-mode (or (:card-mode @db/kanban)
                      :short-card)
        individual (get @db/expanded entity-id)]
    (or (and (= card-mode :short-card)
             individual)
        (and (= card-mode :long-card)
             (not individual)))))

(defn toggle-expand!
  [entity-id]
  (let [expanded? (get @db/expanded entity-id)]
    (if expanded?
      (swap! db/expanded dissoc entity-id)
      (swap! db/expanded assoc entity-id true))))

(defn resolve-column-key-universe
  []
  (->> (grid/resolve-column-key-universe (setup-args))
       (remove (fn [column-key]
                 (and (= column-key :kyc-profile/screening-status)
                      (not (session/screening?)))))))

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))

(defn cleanup
  []
  (when-let [tracker (:tracker-1 @db/contract-kyc-profiles)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-2 @db/contract-kyc-profiles)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-3 @db/contract-kyc-profiles)]
    (r/dispose! tracker))

  (swap! db/contract-kyc-profiles dissoc
         :loaded
         :schema
         :entities
         :entity-types
         :view-settings
         :sorted-grid-entities
         :search
         :kanban
         :sorted-kanban-entities
         :selected
         :grid-entities))

(defn toggle-quick-filters!
  []
  (swap! db/contract-kyc-profiles update :show-quick-filters? not))

(defn system-views
  []
  (concat [{:named-view/settings {:selected-columns (columns/get-default-columns)
                                  :ordered-columns  (columns/get-ordered-default-columns)
                                  :sort-by          :asset/name
                                  :sort-direction   "ASC"
                                  :view-mode        (if (tenant-customizations/capman?)
                                                      :grid-mode
                                                      :board-mode)
                                  :grouped-by       :contract-kyc-profile/status}
            :named-view/name     (loc/tr :all-contract-kyc-profiles)
            :disable-delete?     true
            :db/id               "named-view-all-contract-kyc-profiles"}]
          (when-not (tenant-customizations/capman?)
            [{:named-view/settings {:selected-columns (columns/get-default-columns)
                                    :ordered-columns  (columns/get-ordered-default-columns)
                                    :sort-by          :asset/name
                                    :sort-direction   "ASC"
                                    :view-mode        :grid-mode}
              :named-view/name     (loc/tr :list-contract-kyc-profiles)
              :disable-delete?     true
              :db/id               "named-view-list-contract-kyc-profiles"}])
          [{:named-view/settings {:selected-columns (columns/get-default-columns)
                                  :sort-by          :asset/name
                                  :sort-direction   "ASC"
                                  :view-mode        :grid-mode
                                  :filters          {:contract-kyc-profile/our-kyc-contact
                                                     {:operand           :operand/any-of
                                                      :lower-bound-value #{(:db/id
                                                                             @session/account)}}}}
            :named-view/name     (loc/tr :my-contract-kyc-profiles)
            :disable-delete?     true
            :db/id               "named-view-my-contract-kyc-profiles"}
           {:named-view/settings {:selected-columns (columns/get-default-columns)
                                  :ordered-columns  (columns/get-ordered-default-columns)
                                  :sort-by          :asset/name
                                  :sort-direction   "ASC"
                                  :view-mode        :grid-mode
                                  :filters          {:contract-kyc-profile/status {:operand :operand/any-of
                                                                                   :lower-bound-value
                                                                                   #{:kyc-status/expired}}}}
            :named-view/name     (loc/tr :expired-contract-kyc-profiles)
            :disable-delete?     true
            :db/id               "named-view-contract-kyc-profile-pipeline"}]))

(defn system-views-by-id
  []
  (->> (system-views)
       (map (fn [v]
              [(:db/id v) v]))
       (into {})))

(defn named-views
  []
  (concat (system-views)
          (->> @session/all-named-views
               (cu/only-entity :named-view/view :contract-kyc-grid)
               (sort-by :common/created-on))))

(defn named-view-by-id
  [id]
  (or (get @session/entities id)
      (get (system-views-by-id) id)))

(defn switch-mode!
  [current-mode]
  (let [new-mode (if (= current-mode :grid-mode) :board-mode :grid-mode)]
    (swap! db/view-settings assoc :view-mode new-mode)
    (ls/update-in (ls-path) assoc :view-mode new-mode)))

(defn setup-column-state!
  [view-settings]
  (reset! db/view-settings view-settings)
  (when (nil? @db/selected-columns)
    (let [default-view-settings (->> (named-views)
                                     first
                                     :named-view/settings)]
      (reset! db/view-settings default-view-settings)
      (ls/set-in (ls-path) default-view-settings)))
  (grid/recalculate (assoc (setup-args) :re-sort? true)))

(defn load-view!
  [named-view]                                              ;;named-view entity
  (let [{:keys [named-view/settings]} named-view]
    (ls/set-in (ls-path) settings)
    (setup-column-state! settings)))

(defn contract-kyc-profile-updates
  []
  (let [contract-kyc-profiles @session/contract-kyc-profiles]
    (swap! db/contract-kyc-profiles assoc :grid-entities contract-kyc-profiles)))

(defn permission-updates
  []
  (let [current-permissions @session/current-permissions]
    (when current-permissions
      (precalculated-schema true))))

(defn trigger-recalculation
  []
  (let [permissions-trigger @db/schema
        entities-trigger @db/grid-entities]
    (when (or permissions-trigger entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/contract-kyc-profiles assoc :loaded true)
                    (deref db/contract-kyc-profiles))))))

(defn setup-without-screening
  [closed-atom?]
  (when-not @closed-atom?
    (if (empty? (ls/get-in (ls-path)))
      (load-view! (first (named-views)))
      (reset! db/view-settings (ls/get-in (ls-path))))
    (swap! db/contract-kyc-profiles assoc
           :tracker-1 (r/track! contract-kyc-profile-updates)
           :tracker-2 (r/track! permission-updates)
           :tracker-3 (r/track! trigger-recalculation))
    (setup-column-state! (ls/get-in (ls-path)))))

(defn setup-with-screening
  [closed-atom?]
  (let [kyc-profile-ids (->> @session/contract-kyc-profiles
                             (map :db/id))]
    (kyc-actions/*->get-screening-statuses
      {:kyc-profile-ids kyc-profile-ids}
      (fn [response]
        (let [statuses (-> response
                           :body
                           :statuses)]
          (swap! session/session assoc :kyc-profile-screening-statuses statuses)
          (setup-without-screening closed-atom?)))
      (fn [error]
        (js/console.log "Error getting screening statuses " error)
        (setup-without-screening closed-atom?)))))

(defn setup
  [_closed-atom?]
  (if (empty? (ls/get-in (ls-path)))
    (load-view! (first (named-views)))
    (reset! db/view-settings (ls/get-in (ls-path))))
  (swap! db/contract-kyc-profiles assoc
         :tracker-1 (r/track! contract-kyc-profile-updates)
         :tracker-2 (r/track! permission-updates)
         :tracker-3 (r/track! trigger-recalculation))
  (setup-column-state! (ls/get-in (ls-path))))

(defn save-current-view!
  [name]
  (let [settings @db/view-settings
        msg {:named-view/name     name
             :named-view/view     :contract-kyc-grid
             :named-view/settings settings}]
    (named-view-actions/*create-named-view->
      msg
      identity
      (fn [& a]                                             ;;TODO show error message
        (logging/error "create-named-view failed" a)))))

(defn target-asset-in
  [company-id]
  (let [target-asset-oppos (->> @session/all-oppos
                                (remove :common/disabled?)
                                (cu/only-entity :oppo/target-asset company-id))
        target-asset-holdings (->> @session/all-holdings
                                   (remove :common/disabled?)
                                   (cu/only-entity :holding/target-asset company-id))
        all-target-asset-entities (concat target-asset-oppos
                                          target-asset-holdings)]
    all-target-asset-entities))

(defn mass-delete!
  []
  (-> (conn/sendp! :kyc-profile/mass-action
                   {:action          :delete-kyc-profiles
                    :kyc-profile-ids (grid/get-selected-items (setup-args))})
      (p/then (fn [_resp]
                (swap! db/contract-kyc-profiles assoc :selected {})))
      (p/catch (fn [e]
                 (logging/error "Unable to delete selected kyc-profiles" e)))))

(defn delete-selected-clicked
  [_]
  (modals/open-modal!
    confirmation/modal-key
    {:body          [:div
                     [:p (loc/tr :confirm-delete-selected)]]
     :confirm-text  (loc/tr :confirm-delete-selected-button)
     :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
     :on-confirm    mass-delete!}))

(defn expand-all!
  []
  (swap! db/kanban assoc :card-mode :long-card)
  (reset! db/expanded {}))

(defn collapse-all!
  []
  (swap! db/kanban assoc :card-mode :short-card)
  (reset! db/expanded {}))

(defn open-card-settings!
  []
  (swap! db/kanban assoc :card-settings? true))

(defn close-card-settings!
  []
  (swap! db/kanban dissoc :card-settings?))

(defn approved-process?
  [process]
  (contains? #{:kyc-process.status/approved :kyc-process.status/approved-modified}
             (:kyc-process/status process)))

(defn remove-attachment-questions
  [questions-meta]
  (->> questions-meta
       (remove (fn [[_q question-meta]]
                 (contains? #{:data.type/file :data.type/confirm} (:data/type question-meta))))))

(defn resolve-investor-info-from-process
  [process]
  (let [kyc-profile (some->> @session/all-kyc-profiles
                             (filter (fn [kp]
                                       (some #{(:db/id process)} (:kyc-profile/kyc-processes kp))))
                             (first))
        investor (session/get-entity (:kyc-profile/target kyc-profile))]
    (when investor
      (select-keys investor [:investor/name :db/id :common/external-id]))))

(defn resolve-kyc-display-datas
  [export-questions answers]
  {:display-data (->> answers
                      (keep
                        (fn [[q v]]
                          (when-let [disp (kyc-utils/display-string q (get export-questions q) v)]
                            [q (fu/if-bigdec-then-float disp)])))
                      (into {}))})

(defn resolve-template-process-answers
  [processes template-types export-schema]
  (->> processes
       (filter (fn [process]
                 (contains? (set template-types) (:kyc-process/template process))))
       (keep (fn [process]
               (let [answers (:kyc-process/answers process)]
                 (when (seq answers)
                   (merge answers
                          (resolve-investor-info-from-process process)
                          (resolve-kyc-display-datas export-schema answers))))))))

(defn resolve-question-process-answers
  [processes export-questions export-schema]
  (->> processes
       (mapcat (fn [process]
                 (when-let [process-answers (:kyc-process/answers process)]
                   (let [answer-peps (some->> export-questions
                                              (keep (fn [q]
                                                      (get process-answers q)))
                                              (first))]
                     (some->> answer-peps
                              (keep (fn [pep-answers]
                                      (when (seq pep-answers)
                                        [process pep-answers]))))))))
       (remove nil?)
       (map (fn [[process question-answer]]
              (merge question-answer
                     (resolve-investor-info-from-process process)
                     (resolve-kyc-display-datas export-schema question-answer))))
       (doall)))

(defn resolve-process-answers
  [processes export-schema]
  (let [export-templates (:export-templates export-schema)
        export-questions (:export-questions export-schema)]
    (cond export-templates                                  ;; legal entity or natural person
          (resolve-template-process-answers processes export-templates export-schema)
          export-questions                                  ;; UBO or PEP
          (resolve-question-process-answers processes export-questions export-schema)
          :else
          (logging/error "Could not resolve KYC export schema type " export-schema))))

(defn resolve-schema-export-question-kws
  [schema]
  (->> schema
       (remove-attachment-questions)
       (map first)))

(defn last-approved-process
  [profile]
  (->> profile
       :kyc-profile/kyc-processes
       (keep session/get-entity)
       (filter approved-process?)
       (sort-by :common/created-on)
       (last)))

(defn get-export-sheet-names-data-and-column-keys
  "Returns list of [sheet-name data-rows sorted-column-keywords]"
  [tenant tenant-schema export-schemas keys-and-schemas profiles]
  (let [profile-datas
        (->>
          profiles
          (map
            (fn [profile]
              (merge
                profile
                (->> keys-and-schemas
                     (map
                       (fn [[k fs]]
                         [k
                          (fu/display-data tenant-schema tenant k fs profile :skip-multiplier? true)]))
                     (into {}))))))
        column-keys (->> keys-and-schemas
                         (map first))
        last-approved-processes (->> profile-datas
                                     (map :db/id)
                                     (keep session/get-entity)
                                     (remove :common/disabled?)
                                     (keep last-approved-process))]
    (concat
      [["KYC profiles" profile-datas column-keys]]
      (->> export-schemas
           (map (fn [export-schema]
                  (let [export-questions
                        (dissoc export-schema :export-name :export-questions :export-templates)]
                    [(:export-name export-schema)
                     (resolve-process-answers last-approved-processes export-schema)
                     (concat [:investor/name]
                             (resolve-schema-export-question-kws export-questions))])))))))

(defn process-next-sheet!
  [{:keys [wb
           export-sheet-datas-atom progress-atom progress-rows-atom]
    :as   args}]
  (let [next-sheet (first @export-sheet-datas-atom)]
    (if-not next-sheet
      (let [todays-date (date-formats/format-millis
                          (zap-time/now)
                          (or (:tenant/date-format @session/tenant) (loc/tr :date-format))
                          (or (:tenant/timezone @session/tenant) timezones/default-timezone))
            file-name (loc/tr :export-filename-generic [(loc/tr :entity.type/contract-kyc-profile) todays-date])]
        (ex-workbook/save-workbook wb file-name)
        (reset! progress-atom 1))
      (let [[sheet-name sheet-data sheet-columns] next-sheet]
        (reset! export-sheet-datas-atom (rest @export-sheet-datas-atom))
        (excel/export-sheet-entities
          (assoc args
            :skip-progress-modal? true
            :processed-rows-start-count @progress-rows-atom
            :on-sheet-completed! (fn []
                                   (fu/timeout 150
                                               (fn []
                                                 (process-next-sheet! args))))
            :sheet (ex-workbook/add-sheet wb sheet-name)
            :column-attributes sheet-columns
            :data-map-rows sheet-data))))))

(defn export-kyc-data
  []
  (let [progress-atom (r/atom 0)
        progress-rows-atom (atom 0)
        cancel-atom (atom false)
        timer (timer/create-timer)
        wb (ex-workbook/create-workbook)
        profile-keys (keys-and-schemas)
        sorted-entities (:sorted-kanban-entities @db/contract-kyc-profiles)
        selected-item-ids (when-not (= (:selected @db/contract-kyc-profiles) :all)
                            (some->> (:selected @db/contract-kyc-profiles)
                                     (map (fn [[k s]]
                                            (when s
                                              k)))))
        export-entities (if (and (coll? selected-item-ids)
                                 (seq selected-item-ids))
                          (->> sorted-entities
                               (filter (fn [entity]
                                         (some #{(:db/id entity)} selected-item-ids))))
                          sorted-entities)
        total-rows (->> export-entities
                        (keep second)
                        (map count)
                        (apply +))
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        tenant-template-kws (->> tenant
                                 :tenant/active-kyc-templates)
        export-schemas (template-registry/resolve-export-schemas tenant-template-kws)
        export-questions-lookup (->> export-schemas
                                     (apply merge))
        export-sheet-datas-atom
        (atom (get-export-sheet-names-data-and-column-keys tenant
                                                           tenant-schema
                                                           export-schemas
                                                           profile-keys
                                                           export-entities))
        args {:progress-atom           progress-atom
              :progress-rows-atom      progress-rows-atom
              :export-sheet-datas-atom export-sheet-datas-atom
              :cancel-atom             cancel-atom
              :timer                   timer
              :wb                      wb
              :progress-total-count    total-rows
              :resolve-schema          (fn [k]
                                         (or (when-let [question-schema (get export-questions-lookup k)]
                                               question-schema)
                                             (registry/get-field-schema tenant-schema k)))}]
    (excel/open-export-progress-modal progress-atom cancel-atom)
    (process-next-sheet! args)))

